import { useState, useCallback, useRef, useEffect } from 'react';
import { Edge, applyNodeChanges } from '@xyflow/react';
import { CustomNode } from '../types/FloorPlan';

export const useFloorPlanEditor = () => {
  const [nodes, setNodes] = useState<CustomNode[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [selectedNodes, setSelectedNodes] = useState<CustomNode[]>([]);
  const [mode, setMode] = useState('default');
  const [undoStack, setUndoStack] = useState<{ nodes: CustomNode[], edges: Edge[] }[]>([]);
  const [redoStack, setRedoStack] = useState<{ nodes: CustomNode[], edges: Edge[] }[]>([]);
  const clipboardRef = useRef<CustomNode[]>([]);
  const isUndoingRef = useRef(false);
  const dragStartNodesRef = useRef<CustomNode[]>([]);
  const isDraggingRef = useRef(false);
  const lastSavedStateRef = useRef<{ nodes: CustomNode[], edges: Edge[] } | null>(null);

  const saveToHistory = useCallback((newNodes: CustomNode[], newEdges: Edge[]) => {
    if (isUndoingRef.current) return;

    console.log('Saving to history:', newNodes.length, 'nodes', newEdges.length, 'edges');
    setUndoStack(prev => [...prev, { nodes, edges }]);
    setRedoStack([]);
    setNodes(newNodes);
    setEdges(newEdges);

    // Store current state in localStorage
    localStorage.setItem('floor-plan-editor-state', JSON.stringify({
      nodes: newNodes,
      edges: newEdges,
      timestamp: Date.now()
    }));
  }, [nodes, edges]);

  const resetState = useCallback((newNodes: CustomNode[] = [], newEdges: Edge[] = []) => {
    console.log('Resetting editor state with:', { nodes: newNodes, edges: newEdges });
    setNodes(newNodes);
    setEdges(newEdges);
    setSelectedNodes([]);
    setMode('default');
    setUndoStack([]);
    setRedoStack([]);
    isDraggingRef.current = false;
    
    // Update lastSavedState when resetting with new content
    if (newNodes.length > 0 || newEdges.length > 0) {
      lastSavedStateRef.current = { nodes: newNodes, edges: newEdges };
    }

    // Clear localStorage when resetting to empty state
    if (newNodes.length === 0 && newEdges.length === 0) {
      localStorage.removeItem('floor-plan-editor-state');
      lastSavedStateRef.current = null;
    } else {
      localStorage.setItem('floor-plan-editor-state', JSON.stringify({
        nodes: newNodes,
        edges: newEdges,
        timestamp: Date.now()
      }));
    }
  }, []);

  const hasUnsavedChanges = useCallback(() => {
    if (!lastSavedStateRef.current && (nodes.length > 0 || edges.length > 0)) {
      return true;
    }

    if (lastSavedStateRef.current) {
      const currentState = JSON.stringify({ nodes, edges });
      const savedState = JSON.stringify(lastSavedStateRef.current);
      return currentState !== savedState;
    }

    return false;
  }, [nodes, edges]);

  const markAsSaved = useCallback(() => {
    lastSavedStateRef.current = { nodes, edges };
    localStorage.setItem('floor-plan-editor-state', JSON.stringify({
      nodes,
      edges,
      timestamp: Date.now()
    }));
  }, [nodes, edges]);

  const undo = useCallback(() => {
    if (undoStack.length === 0) return;

    isUndoingRef.current = true;
    const lastState = undoStack[undoStack.length - 1];
    const newUndoStack = undoStack.slice(0, -1);

    setRedoStack(prev => [...prev, { nodes, edges }]);
    setUndoStack(newUndoStack);
    setNodes(lastState.nodes);
    setEdges(lastState.edges);
    setSelectedNodes([]);
    isUndoingRef.current = false;
  }, [nodes, edges, undoStack]);

  const redo = useCallback(() => {
    if (redoStack.length === 0) return;

    isUndoingRef.current = true;
    const nextState = redoStack[redoStack.length - 1];
    const newRedoStack = redoStack.slice(0, -1);

    setUndoStack(prev => [...prev, { nodes, edges }]);
    setRedoStack(newRedoStack);
    setNodes(nextState.nodes);
    setEdges(nextState.edges);
    setSelectedNodes([]);
    isUndoingRef.current = false;
  }, [nodes, edges, redoStack]);

  const getNodes = useCallback(() => {
    return nodes;
  }, [nodes]);

  const getEdges = useCallback(() => {
    return edges;
  }, [edges]);

  const setNodesDirectly = useCallback((newNodes: CustomNode[]) => {
    console.log('Setting nodes directly:', newNodes.length, 'nodes');
    setNodes(newNodes);
  }, []);

  const onNodesChange = useCallback((changes: any) => {
    if (isUndoingRef.current) return;

    console.log('Node changes:', changes);

    // Filter changes to separate selection changes from other changes
    const selectionChanges = changes.filter((change: any) => change.type === 'select');
    const nonSelectionChanges = changes.filter((change: any) => change.type !== 'select');
    
    // Apply all changes to update the nodes state
    const newNodes = applyNodeChanges(changes, nodes);
    
    // Update nodes with the applied changes
    setNodes(newNodes);

    // If there are non-selection changes, handle them differently
    if (nonSelectionChanges.length > 0) {
      // Handle drag operations
      const dragChanges = nonSelectionChanges.filter(change => change.type === 'position');
      if (dragChanges.length > 0) {
        const firstDragChange = dragChanges[0];
        
        // Start of drag
        if (firstDragChange.dragging && !isDraggingRef.current) {
          isDraggingRef.current = true;
          dragStartNodesRef.current = [...nodes];
          return;
        }
        
        // End of drag
        if (!firstDragChange.dragging && isDraggingRef.current) {
          isDraggingRef.current = false;
          if (JSON.stringify(dragStartNodesRef.current) !== JSON.stringify(newNodes)) {
            saveToHistory(newNodes, edges);
          }
          return;
        }

        // During drag - don't save to history
        if (firstDragChange.dragging) {
          return;
        }
      }

      // Handle other changes (like adding, removing, or resizing nodes)
      if (JSON.stringify(nodes) !== JSON.stringify(newNodes)) {
        saveToHistory(newNodes, edges);
      }
    }
  }, [nodes, edges, saveToHistory]);

  const deleteSelectedNodes = useCallback(() => {
    if (selectedNodes.length === 0) return;

    const selectedIds = new Set(selectedNodes.map(node => node.id));
    const newNodes = nodes.filter(node => !selectedIds.has(node.id));
    const newEdges = edges.filter(
      edge => !selectedIds.has(edge.source) && !selectedIds.has(edge.target)
    );

    saveToHistory(newNodes, newEdges);
    setSelectedNodes([]);
  }, [nodes, edges, selectedNodes, saveToHistory]);

  const copySelectedNodes = useCallback(() => {
    clipboardRef.current = selectedNodes.map(node => ({
      ...node,
      id: `${node.id}-copy-${Date.now()}`,
      position: { ...node.position }
    }));
    
    console.log('Copied nodes to clipboard:', clipboardRef.current.map(n => n.id));
  }, [selectedNodes]);

  const pasteNodes = useCallback(() => {
    if (clipboardRef.current.length === 0) return;

    const offsetX = 20;
    const offsetY = 20;
    
    const newNodes = clipboardRef.current.map(node => ({
      ...node,
      position: {
        x: node.position.x + offsetX,
        y: node.position.y + offsetY
      }
    }));

    console.log('Pasting nodes from clipboard:', newNodes.map(n => n.id));
    saveToHistory([...nodes, ...newNodes], edges);
    setSelectedNodes(newNodes);
  }, [nodes, edges, saveToHistory]);

  const loadSavedState = useCallback(() => {
    try {
      const savedState = localStorage.getItem('floor-plan-editor-state');
      if (savedState) {
        const { nodes: savedNodes, edges: savedEdges, timestamp } = JSON.parse(savedState);
        const isExpired = Date.now() - timestamp > 24 * 60 * 60 * 1000;
        
        if (!isExpired) {
          resetState(savedNodes, savedEdges);
          return true;
        }
      }
    } catch (error) {
      console.error('Error loading saved state:', error);
    }
    return false;
  }, [resetState]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Voorkom toetsenbord acties als er een input element actief is
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }

      if (event.ctrlKey || event.metaKey) {
        switch (event.key.toLowerCase()) {
          case 'c':
            event.preventDefault();
            copySelectedNodes();
            break;
          case 'v':
            event.preventDefault();
            pasteNodes();
            break;
          case 'z':
            event.preventDefault();
            if (event.shiftKey) {
              redo();
            } else {
              undo();
            }
            break;
          case 'y':
            event.preventDefault();
            redo();
            break;
          case 'a':
            event.preventDefault();
            // Select all nodes
            setSelectedNodes([...nodes]);
            break;
        }
      } else if (event.key === 'Delete' || event.key === 'Backspace') {
        // Voorkom dat de pagina teruggaat bij backspace
        if (event.target === document.body) {
          event.preventDefault();
        }
        deleteSelectedNodes();
      } else if (event.key === 'Escape') {
        // Clear selection on Escape key
        setSelectedNodes([]);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [copySelectedNodes, pasteNodes, deleteSelectedNodes, undo, redo, nodes]);

  useEffect(() => {
    loadSavedState();
  }, [loadSavedState]);

  return {
    nodes,
    edges,
    selectedNodes,
    setSelectedNodes,
    mode,
    setMode,
    onNodesChange,
    saveToHistory,
    resetState,
    canUndo: undoStack.length > 0,
    canRedo: redoStack.length > 0,
    undo,
    redo,
    copySelectedNodes,
    pasteNodes,
    deleteSelectedNodes,
    hasUnsavedChanges,
    markAsSaved,
    loadSavedState,
    getNodes,
    getEdges,
    setNodes: setNodesDirectly
  };
};
